import { BaseService } from "../BaseService";
import { ChartFilter } from "@/types/breeze/ChartConfiguration";
import {
  CreateMaterialisedViewRequest,
  MaterialisedView,
  MaterialisedViewColumn,
  MaterialisedViewData,
  UpdateMaterialisedViewRequest,
} from "@/types/breeze/MaterialisedView";

interface MaterialisedViewWireResponse extends Omit<MaterialisedView, "columns" | "base_filters"> {
  columns: string;
  base_filters?: string | null;
}

export class MaterialisedViewService extends BaseService {
  public async getAll(baseEntityName?: string): Promise<MaterialisedView[]> {
    const query = baseEntityName ? `?base_entity_name=${encodeURIComponent(baseEntityName)}` : "";
    const response = await this.getRequest<MaterialisedViewWireResponse[]>(`/materialised-views${query}`);
    return response.map(hydrateView);
  }

  public async getById(id: number): Promise<MaterialisedView> {
    const response = await this.getRequest<MaterialisedViewWireResponse>(`/materialised-views/${id}`);
    return hydrateView(response);
  }

  public async create(request: CreateMaterialisedViewRequest): Promise<MaterialisedView> {
    const response = await this.postRequest<CreateMaterialisedViewRequest, MaterialisedViewWireResponse>(
      "/materialised-views", request, "View saved",
    );
    return hydrateView(response);
  }

  public async update(id: number, request: UpdateMaterialisedViewRequest): Promise<MaterialisedView> {
    const response = await this.putRequest<UpdateMaterialisedViewRequest, MaterialisedViewWireResponse>(
      `/materialised-views/${id}`, request, "View updated",
    );
    return hydrateView(response);
  }

  public async deleteById(id: number): Promise<void> {
    await this.deleteRequest(`/materialised-views/${id}`, "View deleted");
  }

  public async getData(id: number, page = 1, pageSize?: number): Promise<MaterialisedViewData> {
    const params = new URLSearchParams({ page: String(page) });
    if (pageSize) params.set("page_size", String(pageSize));
    return this.getRequest<MaterialisedViewData>(`/materialised-views/${id}/data?${params}`);
  }

  public async previewConfig(request: CreateMaterialisedViewRequest & { page?: number; page_size?: number }): Promise<MaterialisedViewData> {
    return this.postRequest<typeof request, MaterialisedViewData>(
      "/materialised-views/preview-config", request, undefined, true, true, true,
    );
  }
}

function hydrateView(wire: MaterialisedViewWireResponse): MaterialisedView {
  return {
    ...wire,
    columns: parseJson<MaterialisedViewColumn[]>(wire.columns) ?? [],
    base_filters: parseJson<ChartFilter[]>(wire.base_filters ?? null),
  };
}

function parseJson<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) || typeof parsed === "object" ? (parsed as T) : null;
  } catch {
    return null;
  }
}

export const materialisedViewService = new MaterialisedViewService();
